import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  ClipboardList, ArrowLeft, RefreshCw, User, Clock, Settings, Calculator
} from 'lucide-react';

export default function TributarioLogs() {
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [filtro, setFiltro] = useState('');
  
  const navigate = useNavigate();

  useEffect(() => {
    loadLogs();
  }, [filtro]);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (filtro) params.tipo = filtro;
      
      const response = await api.get('/tributario/logs', { params });
      setLogs(response.data.logs || []);
    } catch (error) {
      console.error('Erro ao carregar logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTipoBadge = (tipo) => {
    const configs = {
      config_criada: { bg: 'bg-green-100 text-green-700', icon: Settings, label: 'Config Criada' },
      config_alterada: { bg: 'bg-blue-100 text-blue-700', icon: Settings, label: 'Config Alterada' },
      apuracao_realizada: { bg: 'bg-purple-100 text-purple-700', icon: Calculator, label: 'Apuração' }
    };
    const config = configs[tipo] || { bg: 'bg-gray-100 text-gray-700', icon: ClipboardList, label: tipo };
    const Icon = config.icon;
    
    return (
      <Badge className={`${config.bg} gap-1`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  return (
    <div className="p-6 space-y-6" data-testid="tributario-logs">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/tributario')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ClipboardList className="w-6 h-6" />
            Logs e Auditoria
          </h1>
          <p className="text-muted-foreground">Histórico de alterações tributárias</p>
        </div>
        <Button variant="outline" onClick={loadLogs}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Atualizar
        </Button>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex gap-4 items-end">
            <div className="space-y-2">
              <Label>Tipo de Operação</Label>
              <Select 
                value={filtro || '_all'} 
                onValueChange={(v) => setFiltro(v === '_all' ? '' : v)}
              >
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">Todas</SelectItem>
                  <SelectItem value="config_criada">Configuração Criada</SelectItem>
                  <SelectItem value="config_alterada">Configuração Alterada</SelectItem>
                  <SelectItem value="apuracao_realizada">Apuração Realizada</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabela de Logs */}
      <Card>
        <CardHeader>
          <CardTitle>Registros ({logs.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">
              {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12" />)}
            </div>
          ) : logs.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">Nenhum log encontrado.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data/Hora</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Usuário</TableHead>
                    <TableHead>Detalhes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map(log => (
                    <TableRow key={log.id}>
                      <TableCell className="whitespace-nowrap">
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Clock className="w-3 h-3" />
                          {new Date(log.created_at).toLocaleString('pt-BR')}
                        </div>
                      </TableCell>
                      <TableCell>
                        {getTipoBadge(log.tipo)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <User className="w-3 h-3 text-muted-foreground" />
                          {log.usuario_nome || 'Sistema'}
                        </div>
                      </TableCell>
                      <TableCell className="max-w-[300px]">
                        {log.tipo === 'apuracao_realizada' && (
                          <span className="text-sm">
                            Competência: <strong>{log.competencia}</strong>
                            {log.total_tributos !== undefined && (
                              <> | Total: <strong>R$ {log.total_tributos?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</strong></>
                            )}
                          </span>
                        )}
                        {log.tipo === 'config_criada' && (
                          <span className="text-sm text-green-600">Nova configuração tributária criada</span>
                        )}
                        {log.tipo === 'config_alterada' && (
                          <span className="text-sm text-blue-600">Parâmetros tributários alterados</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
